/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import _get from 'lodash/get'
import React from 'react'
import { Text } from 'hocs/localization'
import { getFormattedTextOrDefault } from 'utils/printedData'
import { SUBSCRIPTION_PLAN_TYPE } from 'constants/subscription'
import { toCurrency } from 'utils/price'
import { isContractExpired } from 'utils/date'
import * as styles from './CurrentSubscription.scss'
import { EMPTY_OBJECT } from 'constants/frozenObjects'

interface IProps {
  plan: any,
  t: Text,
  onGoToRenewalProcess: () => void,
  onGoToChangePlanProcess: () => void,
  onGoToEnrollPlanProcess: () => void,
  currency: string,
  businessName: string,
  userDetails: any,
  nextBilling: string | null,
  lastBilling: string | null,
  address: string,
  contractType: string,
  isEnrollAvailable: boolean,
  userSubscription: any,
  futureSubscription: any,
}

const CurrentSubscription: React.SFC<IProps> = ({
  plan,
  t,
  onGoToRenewalProcess,
  onGoToChangePlanProcess,
  onGoToEnrollPlanProcess,
  currency,
  businessName,
  userDetails,
  nextBilling,
  lastBilling,
  address,
  contractType,
  isEnrollAvailable,
  userSubscription,
  futureSubscription,
}) => {

  const termEnd = _get(userSubscription, 'termsEndDate')
  const isCurrentContractExpired = futureSubscription === EMPTY_OBJECT && isContractExpired(termEnd)
  const isRenewalButtonEnabled = userSubscription.isRenewalProcessAvailable

  return (
  <div className={styles.ManageAccount}>
    <div className={styles.planimg}>
      <img src={plan.imageUri} alt={plan.name}/>
    </div>
    <div className={styles.planimgtext}>
      <ul>
        <li className={styles.planItem}>
          <span>
            {t('subscription')}:
          </span>
          <span>
            {t('subscriptionName', { planName: plan.name, currency, planPrice: toCurrency(plan.price) })}
          </span>
        </li>
        <li className={styles.planItem}>
          <span>{t('contractType')}:</span>
          {isCurrentContractExpired && (
            <span className={styles.expiredStatus}>Expired</span>
          )}
          {!isCurrentContractExpired && (
            <span>{contractType}</span>
          )}
        </li>
        <li className={styles.planItem}>
          <span>{t('nextPayment')}:</span>
          <span>{getFormattedTextOrDefault(nextBilling => nextBilling)(nextBilling)}</span>
        </li>
        <li className={styles.planItem}>
          <span>{t('lastPayment')}:</span>
          <span>{getFormattedTextOrDefault(lastBilling => lastBilling)(lastBilling)}</span>
        </li>
        {address && (
          <li className={styles.planItem}>
            <span>{t('address')}:</span>
            <span>{address}</span>
          </li>
        )}
        {businessName && (
          <li className={styles.planItem}>
            <span>{t('businessName')}:</span>
            <span>{businessName}</span>
          </li>
        )}
        {userDetails.creditCard && (
          <li className={styles.planItem}>
            <span>{t('cardOnFile')}:</span>
            <div className={styles.cardData}>
              <span>*{userDetails.creditCard.last4Digits}</span>
              <span>{userDetails.creditCard.expirationMonth}/{userDetails.creditCard.expirationYear}</span>
            </div>
          </li>
        )}
        <li className={styles.planItem}>
          <span>{t('emailAddress')}:</span>
          <span>{userDetails.email}</span>
        </li>
        {(!isCurrentContractExpired && isRenewalButtonEnabled) && (
          <li className={styles.planItem}>
            <div className={styles.link} onClick={onGoToRenewalProcess}>
              <strong>{t('renewalOptions')}</strong>
            </div>
          </li>
        )}
        {(isEnrollAvailable) && (
          <li className={styles.planItem}>
            <div className={styles.link} onClick={onGoToEnrollPlanProcess}>
              <strong>{t('enrollAgain')}</strong>
            </div>
          </li>
        )}
      </ul>
    </div>
  </div>
  )
}

export default CurrentSubscription
